import { CallModel, CallState } from "@fidget/common/models/call/callModel";
import { MousePositionInfoModel } from "@fidget/common/models/mouse-position-info";
import { TrackInfo } from "@fidget/common/services/AbstractCallService";
import { mediaDevicesService } from "@fidget/common/services/mediaDevicesService";
import { MediaService } from "@fidget/common/services/mediaService";
import { logger } from "@fidget/common/utils";
import { flow, Instance, types } from "mobx-state-tree";
import { servicesGetters } from "../utils";

const ForeViewCallModel = types
  .model({
    videoExpanded: false,
    mousePositionInfo: types.maybe(MousePositionInfoModel)
  })
  .actions((self) => {
    return {
      toggleVideoExpanded() {
        self.videoExpanded = !self.videoExpanded;
      },
      resetViewState() {
        self.videoExpanded = false;
      }
    };
  });

export const ForeCallModel = types
  .compose("ForeCallModel", CallModel, ForeViewCallModel)
  .views(servicesGetters)
  .actions((self) => {
    const superHandleRemoteTrackReceived = self.handleRemoteTrackReceived;
    return {
      handleRemoteTrackReceived(trackInfo: TrackInfo) {
        superHandleRemoteTrackReceived(trackInfo);
        if (
          trackInfo.enabled !== undefined &&
          trackInfo.enabled &&
          trackInfo.content === "screen"
        ) {
          self.videoExpanded = true;
        }
      }
    };
  })
  .actions((self) => {
    const mediaService = MediaService.getInstance();

    return {
      connect: flow(function* (agentId: string) {
        if (!self.isNoCall) {
          logger.warn("CallStore: can't call when there is a call in progress");
          return;
        }

        logger.debug("CallStore: call");
        self.resetViewState();
        const track = yield mediaService.getMicrophoneTrack();
        if (track) {
          self.localMicrophone.setMediaStreamTrack(track);
          self.localMicrophone.enable();
          self.remoteAudio.enable();
          try {
            self.state = CallState.CONNECTING;
            const call = yield self.callService.connect(agentId, track);
            self.handleConnecting(call);
          } catch (e) {
            logger.error({ message: "Failed to create a call", error: e });
            self.handleEnded();
          }
        } else {
          // check if microphone permission was rejected
          const perms = yield mediaDevicesService.reloadPermissions();
          if (!perms.isWebsiteHasMicrophonePermissions) {
            self.localMicrophone.denyPermission();
          }
        }
      }),

      hangup: flow(function* () {
        if (!self.isNoCall && self.callId) {
          logger.debug("CallStore: cancel");
          self.resetViewState();
          yield self.callService.leave({ callId: self.callId });
        }
      })
    };
  })
  .actions((self) => {
    return {
      afterCreate() {
        self.callService.answered$.subscribe((userCall) => self.handleAnswered(userCall));
      }
    };
  });

export type IForeCallStore = Instance<typeof ForeCallModel>;
